import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { createApiCall } from 'services';

export type CertificateActivityUserType = 'customer' | 'admin' | 'anonymous';

export interface CertificateActivityLogUser {
  type?: CertificateActivityUserType;
  id: string;
  first_name: string | null;
  last_name: string | null;
  email?: string;
  avatar?: string | null;
  role?: string;
}

export interface CertificateActivityLog {
  id: string;
  type: string;
  created_at: string;
  metadata?: Record<string, unknown> | null;
  user?: CertificateActivityLogUser | null;
  user_to?: CertificateActivityLogUser | null;
  /**
   * Certificate status this event left the certificate in, read from the log's own
   * `snapshot.status` by the API (VERA-978).
   *
   * OPTIONAL and often absent — only some of the paths that write action logs record a
   * status, so coverage varies a lot per certificate (backfill tracked as VRG-196).
   * Typed as a plain string rather than the `CertificateStatus` union because it is a
   * wire value: an unrecognised status must degrade to "no chip", not to a type error.
   */
  certificate_status?: string | null;
}

export interface CertificateActivityResponse {
  data: CertificateActivityLog[];
  total: number;
  limit: number;
  offset: number;
}

interface UseCertificateActivityParams {
  certificateId?: string;
  limit?: number;
  offset?: number;
  sort?: 'ASC' | 'DESC';
  enabled?: boolean;
}

const getCertificateActivity = async (
  certificateId: string,
  params: UseCertificateActivityParams
): Promise<CertificateActivityResponse> => {
  const response = await createApiCall({
    method: 'GET',
    url: `/certificates/customer/activity/${certificateId}`,
    params: {
      limit: params.limit || 20,
      offset: params.offset || 0,
      sort: params.sort || 'DESC',
    },
  });

  return response.data as CertificateActivityResponse;
};

/**
 * ONE definition of this query's key and fetcher, so a prefetch cannot miss.
 *
 * The certificate page warms this cache before the user opens the Activity
 * screen, and a prefetch is only worth anything if it lands on the exact key the
 * screen then reads. Hand-writing the key at the prefetch site is how that
 * silently stops being true — the scan-logs query had precisely this failure
 * (page fetched `limit: 500`, screen asked for `limit: 20`, different key, full
 * refetch of data already in memory), and nothing reported it because a cache
 * miss just looks like a slow screen.
 *
 * Both callers go through here, so the key is right by construction.
 */
export const certificateActivityQuery = (params: UseCertificateActivityParams) => ({
  queryKey: ['certificate-activity', params.certificateId, params] as const,
  queryFn: () => getCertificateActivity(params.certificateId!, params),
});

export const useCertificateActivity = (
  params: UseCertificateActivityParams = {}
): UseQueryResult<CertificateActivityResponse, Error> => {
  return useQuery({
    ...certificateActivityQuery(params),
    enabled: params.enabled !== false && !!params.certificateId,
  });
};
